import type { Attachment } from "@/types/Attachment";
import type { PortalConfig } from "@/types/PortalConfig";
import type { PortalTicket, TicketManifest } from "@/types/Ticket";
import { getBootChannel } from "@/lib/boot-screen";

export interface PortalResponse {
  guestName: string;
  /** The guest's own stored language (BCP-47 or short code). Preferred over
   *  the event's defaultLocale and the browser locale when picking the UI
   *  language. May be null/absent when the guest has no language set. */
  guestLanguage?: string | null;
  eventName: string;
  attachments: Attachment[];
  portalConfig?: PortalConfig;
  /**
   * Flat variable context for resolving `{{variable}}` placeholders in the
   * portal's custom texts, keyed by the same registry insert-keys the email
   * builder uses (firstName, eventName, companyName, …) plus a nested
   * `additionalFields` object for `{{additionalFields.<name>}}`. Absent keys
   * (appointment / seating / URLs — no such data in a documents portal)
   * resolve to empty. May be absent on older backends.
   */
  variables?: Record<string, unknown>;
  /**
   * Ticket block (QR + wallet passes + PDF). `null`/absent = no section and the
   * lazy ticket chunk is never requested. Only present when the event enabled
   * the ticket AND at least one person of the party passes the door formula.
   */
  ticket?: PortalTicket | null;
  /**
   * Art. 50 Abs. 2 KI-VO: the portal's AI disclosure (WICG vocabulary) — the
   * portal mark OR the event mark, decided by portal-backend
   * (lib/ai-disclosure.ts). Top level because `portalConfig` can be absent.
   * Absent = unmarked (also every reply from an older worker). Written into
   * `<meta name="ai-disclosure">` by `injectPortalHead`.
   */
  aiDisclosure?: PortalAiDisclosure;
}

/** WICG `ai-disclosure` values the portal emits; nothing else is ever written. */
export type PortalAiDisclosure = "ai-generated" | "ai-assisted";

export interface DownloadUrlResponse {
  url: string;
  expiresAt: string;
}

/** `GET /portal/:token/boot` — what the loading screen may draw before the data lands. */
export interface PortalBootResponse {
  /** Absolute URL of the operator's loading animation, or null → spinner. */
  loadingImageUrl: string | null;
  /** Hex page background, or null → the SPA default. */
  backgroundColor: string | null;
}

// Portal data API base — always same-origin `/portal/:token`:
//  - canonical hosts (portal.veltric.app / portal-fallback.veltric.app): the
//    portal-backend Worker route serves `/portal/*` directly.
//  - customer custom domains (Cloudflare for SaaS): the Worker route does not
//    match the custom hostname, so a Pages Function (functions/portal/[[path]].ts)
//    proxies `/portal/*` to the canonical origin server-side.
//  - dev: the vite server proxies `/portal` to the local backend.
const BASE = "/portal";

export class PortalApiError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
  }
}

export async function fetchPortal(token: string): Promise<PortalResponse> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}`, {
    method: "GET",
    credentials: "omit", // no cookies — portal is stateless
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: "unknown" }));
    throw new PortalApiError(body.message ?? "unknown", res.status);
  }
  return (await res.json()) as PortalResponse;
}

/**
 * Fired in parallel with {@link fetchPortal}; the loading screen draws the
 * operator's animation and background from this (cheaper) answer. Non-
 * essential: the caller ignores every failure (an older worker answers 404).
 *
 * ONE /boot per page load: on `/p/:token` the HTML's inline script has already
 * issued it before this bundle ran (src/lib/boot-screen.ts) — its promise is
 * consumed here instead of a second request. A null answer from the channel
 * is a failure, never a re-fetch: the caller runs `retry: false`, and by the
 * time a retry fired the data would be there anyway.
 */
export async function fetchPortalBoot(token: string): Promise<PortalBootResponse> {
  const ch = getBootChannel(token);
  if (ch) {
    const b = await ch.boot;
    if (b) return b;
    throw new PortalApiError("unknown", 0);
  }
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/boot`, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new PortalApiError("unknown", res.status);
  return (await res.json()) as PortalBootResponse;
}

export async function fetchDownloadUrl(token: string, fileId: string): Promise<DownloadUrlResponse> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/download/${encodeURIComponent(fileId)}`, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: "unknown" }));
    throw new PortalApiError(body.message ?? "unknown", res.status);
  }
  return (await res.json()) as DownloadUrlResponse;
}

/** `variant=file`: the presigned R2 link to the rendered .ics / .pdf / .pkpass. */
export interface DocumentFileLink {
  url: string;
  /** ISO — the presign's real end, never "now + 300 s" (a cached presign may have 60 s left). */
  expiresAt: string;
  filename: string;
}

/** `variant=google`: the Google Wallet save link. */
export interface DocumentGoogleLink {
  saveUrl: string;
}

/**
 * How long a document tap may wait for the worker before it counts as "network
 * down". Above the worker's own worst case (Mongo socket + its 15 s RPC
 * deadline — a slow but live answer must arrive as itself, e.g. "busy"), so
 * this only ever catches a connection that is really dead.
 */
export const DOCUMENT_FETCH_TIMEOUT_MS = 35_000;

/**
 * Renders one document template of an attachment for the token's guest and
 * answers its link. Called on a TAP only (the render is per guest and the
 * Google variant upserts the pass object) — never prefetched. `no-store` like
 * the ticket manifest: every answer carries a fresh presign. The shape is
 * checked by the caller (components/documents/documentUtils.ts), so this stays
 * small in the cold bundle.
 */
export async function fetchDocumentUrl(
  token: string,
  attachmentId: string,
  templateId: string,
  variant: "file" | "google",
): Promise<DocumentFileLink | DocumentGoogleLink> {
  // A dead mobile connection never settles, and the row's "Preparing…" has no
  // onClick: abort after DOCUMENT_FETCH_TIMEOUT_MS and fail as "network down"
  // (a TypeError, like fetch's own), which the row offers to retry. A plain
  // timer, not AbortSignal.timeout (older iOS Safari lacks it); cleared on
  // settle, body included.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), DOCUMENT_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(
      `${BASE}/${encodeURIComponent(token)}/document/${encodeURIComponent(attachmentId)}/${encodeURIComponent(templateId)}?variant=${variant}`,
      {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        headers: { Accept: "application/json" },
        signal: ctrl.signal,
      },
    );
    if (!res.ok) {
      // `?.`: a JSON `null` body must stay an API error, not turn into a TypeError
      // (which the rows read as "network down").
      const body = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new PortalApiError(typeof body?.message === "string" ? body.message : "unknown", res.status);
    }
    return (await res.json()) as DocumentFileLink | DocumentGoogleLink;
  } catch (err) {
    if (ctrl.signal.aborted) throw new TypeError("document request timed out");
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Presigned pass/PDF URLs + Google save URL for the guest's party. Always
 * `Cache-Control: no-store` upstream; the links expire after 300 s, so the
 * caller (useTicketManifest) re-fetches ahead of `expiresAt`.
 */
export async function fetchTicketManifest(token: string): Promise<TicketManifest> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/ticket`, {
    method: "GET",
    credentials: "omit",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: "unknown" }));
    throw new PortalApiError(body.message ?? "unknown", res.status);
  }
  return (await res.json()) as TicketManifest;
}

/**
 * Tells the backend that a ticket action was tapped, for the organiser's guest
 * timeline. It records the TAP — whether the pass reached the wallet is not
 * knowable. Fire-and-forget: `keepalive` because the Apple link navigates this
 * very tab away, and every failure is swallowed — a lost log line must never
 * cost the guest their ticket. The backend answers 204 whatever happened.
 */
export function sendPortalEvent(token: string, kind: "apple" | "google" | "pdf", guestId: string): void {
  try {
    void fetch(`${BASE}/${encodeURIComponent(token)}/event`, {
      method: "POST",
      credentials: "omit",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind, guestId }),
    }).catch(() => undefined);
  } catch {
    /* fetch unavailable or threw synchronously */
  }
}
